import { Box, Button, Checkbox, Flex, Image, Modal, Paper, Switch, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { env } from '../../env.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { notEmpty } from '../../lib/notEmpty.ts';
import { useEventItem } from '../hooks/useEventItem.ts';
import { useEventItems } from '../hooks/useEventItems.ts';

const formatDate = (date: Date | undefined) => {
  return date ? dayjs(date).format('ddd DD/MM hh:mm') : 'Not specified';
};

const formatShortDate = (date: Date | undefined) => {
  return date ? dayjs(date).format('ddd DD/MM YYYY') : 'Not specified';
};

interface EventDeleteProps {
  eventId?: string;
  /** When true, auto-enable "delete series" with all events selected */
  deleteAll?: boolean;
  onClose: () => void;
}

export const EventDelete: FC<EventDeleteProps> = ({ eventId, deleteAll = false, onClose }) => {
  const { t } = useI18n();

  const { query: eventQuery } = useEventItem(eventId ?? '');
  const [deleteSeries, setDeleteSeries] = useState(deleteAll);
  const [selectedEventIds, setSelectedEventIds] = useState<Record<string, boolean>>({});

  const { query: seriesEventsQuery, removeMutation: deleteEventsMutation } = useEventItems({
    queryActive: !!eventQuery?.data?.eventGroupId,
    filters: { eventGroupId: eventQuery.data?.eventGroupId },
  });

  const event = eventQuery.data;
  const otherEventsInSeries = seriesEventsQuery.data?.filter((item) => item.id !== eventId);

  // Auto-select all series events when deleteAll is true
  useEffect(() => {
    if (deleteAll && otherEventsInSeries && otherEventsInSeries.length > 0) {
      setSelectedEventIds(Object.fromEntries(otherEventsInSeries.map((e) => [e.id, true])));
    }
  }, [deleteAll, otherEventsInSeries]);

  const handleDelete = () => {
    const eventIdsToDelete = Object.entries(selectedEventIds)
      .map(([id, isSelected]) => (isSelected ? id : undefined))
      .filter(notEmpty);

    deleteEventsMutation.mutate([...eventIdsToDelete, eventId].filter(notEmpty), {
      onSuccess: () => {
        setDeleteSeries(false);
        onClose();
      },
    });
  };

  const handleCancel = () => {
    setDeleteSeries(false);
    onClose();
  };

  const handleDeleteSeriesSwitch = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;
    setDeleteSeries(isChecked);
    if (isChecked && otherEventsInSeries) {
      setSelectedEventIds(Object.fromEntries(otherEventsInSeries.map((e) => [e.id, true])));
    } else {
      setSelectedEventIds({});
    }
  };

  const handleSelectAllEvents = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;

    if (isChecked && otherEventsInSeries) {
      setSelectedEventIds(Object.fromEntries(otherEventsInSeries.map((event) => [event.id, true])));
      return;
    }

    setSelectedEventIds({});
  };

  const handleSelectEvent = (eventId: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;
    setSelectedEventIds((currentIds) => ({
      ...currentIds,
      [eventId]: isChecked,
    }));
  };

  return (
    <Modal
      centered
      opened
      size="lg"
      title={
        <Title component="span" order={3}>
          {t('event-delete_modal-title') ?? 'Remove event'}
        </Title>
      }
      onClose={() => {
        setDeleteSeries(false);
        onClose();
      }}
    >
      {eventId && event && (
        <Box>
          <Text mb="xs">{t('event-delete_removing-event') ?? 'You are removing the following event:'}</Text>
          <Paper withBorder p="xxs">
            <Flex gap="xxs">
              {event.image && (
                <Image
                  alt={event.name || ''}
                  crossOrigin="anonymous"
                  h={80}
                  radius="xs"
                  src={`${env.VITE_DIRECTUS_API_URL}/assets/${event.image}?width=160&height=160&fit=cover`}
                  w={80}
                />
              )}
              <Box>
                <Title mb="xxxs" order={5}>
                  {(event.name || t('event-delete_event-name-not-specified')) ?? 'Event name not specified'}
                </Title>
                <Text size="xxs">
                  <Text span fw={500} size="xxs">
                    {t('event-delete_from') ?? 'From:'}
                  </Text>{' '}
                  {formatDate(event.start)}
                </Text>
                <Text size="xxs">
                  <Text span fw={500} size="xxs">
                    {t('event-delete_to') ?? 'To:'}
                  </Text>{' '}
                  {formatDate(event.end)}
                </Text>
              </Box>
            </Flex>
          </Paper>
          {otherEventsInSeries && otherEventsInSeries.length > 0 && (
            <Switch
              checked={deleteSeries}
              color="grape"
              label={t('event-delete_delete-series-switch') ?? 'Delete other recurring events in series'}
              mb="xxs"
              mt="xs"
              onChange={handleDeleteSeriesSwitch}
            />
          )}

          {deleteSeries && (
            <Box>
              <Text c="dimmed" mb="xxxs" size="xxs">
                {t('event-delete_other-events-caption') ?? 'Other events in this series that will also be deleted'}
              </Text>
              <Table verticalSpacing="xxxs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th p={0} w={32}>
                      <Checkbox
                        checked={otherEventsInSeries?.every((event) => selectedEventIds[event.id])}
                        color="red.5"
                        size="sm"
                        onChange={handleSelectAllEvents}
                      />
                    </Table.Th>
                    <Table.Th fz="xxs">{t('event-delete_event-name') ?? 'Name'}</Table.Th>
                    <Table.Th fz="xxs" style={{ textAlign: 'right' }}>
                      {t('event-delete_event-date') ?? 'Date'}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {otherEventsInSeries?.map((event) => (
                    <Table.Tr key={event.id}>
                      <Table.Td p={0}>
                        <Checkbox
                          checked={selectedEventIds[event.id] ?? false}
                          color="red.5"
                          size="sm"
                          onChange={handleSelectEvent(event.id)}
                        />
                      </Table.Td>
                      <Table.Td fz="xxs">{event.name}</Table.Td>
                      <Table.Td fz="xxs" style={{ textAlign: 'right' }}>
                        {formatShortDate(event.start)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}

          <Text mt="xxs" size="xxs">
            {t('event-delete_confirm-message')}
          </Text>
          <Flex gap="xxxs" justify="flex-end" mt="xxs">
            <Button color="gray" radius="xl" size="md" variant="subtle" onClick={handleCancel}>
              {t('event-delete_cancel-button') ?? 'Cancel'}
            </Button>
            <Button color="red" loading={deleteEventsMutation.isPending} radius="xl" size="md" onClick={handleDelete}>
              {t('event-delete_delete-button') ?? 'Delete'}
            </Button>
          </Flex>
        </Box>
      )}
    </Modal>
  );
};
